<script setup lang="ts">
    import LeafMask from './utils/LeafMask.vue';
    import 'vue-image-zoomer/dist/style.css';
    import StoryblokImage from './utils/StoryblokImage.vue';

    const props = defineProps<{
        blok: AImageComponentStoryblok;
    }>();

    const bannerAspectRatios = ['24-4', '24-6', '16-6'];

    const imageType = computed(() => {
        if (props.blok.aspectRatioDesktop && bannerAspectRatios.includes(props.blok.aspectRatioDesktop)) {
            return 'banner';
        } else {
            return 'mainPicture';
        }
    });

    const customAspectRatio = ref(props.blok.aspectRatio);
    const customAspectDesktopRatio = ref(props.blok.aspectRatioDesktop);
    const customBlur = ref(props.blok.imageBlur);

    const imageClasses = computed(() => {
        const imageFit = props.blok.imageFit === 'contain' ? '--image-size-contain' : '';
        const aspectRatio = props.blok.aspectRatio ? `--aspect-ratio-${customAspectRatio.value}` : '';
        const aspectRatioDesktop = props.blok.aspectRatio
            ? `--aspect-ratio-desktop-${customAspectDesktopRatio.value}`
            : '';
        const blur = props.blok.imageBlur ? `--blur-${customBlur.value}` : '';
        const round = props.blok.roundCorners ? '--round-corner' : '';
        const isMasked = props.blok.leafMask ? '--is-masked' : '';
        const isMaskedFlipped = props.blok.flipLeafMask ? '--is-masked--flipped' : '';
        const zoomImage = props.blok.imageZoom ? '--zoom' : '';

        return [imageFit, aspectRatio, aspectRatioDesktop, blur, round, isMasked, isMaskedFlipped, zoomImage].filter(
            Boolean
        ); // Removes any empty strings
    });
</script>

<template>
    <div
        v-if="!(blok.linkValue?.story?.full_slug || blok.linkValue?.url) && !blok.linkValue?.id"
        v-editable="blok"
        class="image-wrapper"
        :class="imageClasses"
    >
        <LeafMask
            v-if="blok.leafMask"
            :flip-leaf-mask="blok.flipLeafMask"
        />

        <StoryblokImage
            v-if="blok.image?.filename && !blok.imageZoom"
            :media="blok.image"
            :type="imageType"
            :alt="blok.image.alt ? blok.image.alt : ''"
        />

        <StoryblokImage
            v-else
            :media="blok.image"
            :type="imageType"
            :alt="blok.image?.alt ? blok.image.alt : ''"
            :zoom="true"
        />
    </div>

    <a
        v-else
        class="image-wrapper --zoom"
        :class="imageClasses"
        :href="useStoryblokUrl(blok.linkValue)"
        :target="blok.linkValue?.target"
    >
        <LeafMask
            v-if="blok.leafMask"
            :flip-leaf-mask="blok.flipLeafMask"
        />

        <StoryblokImage
            v-if="blok.image.filename && !blok.imageZoom"
            :media="blok.image"
            :type="imageType"
            :alt="blok.image.alt ? blok.image.alt : ''"
        />

        <StoryblokImage
            v-else
            :media="blok.image"
            :type="imageType"
            :alt="blok.image.alt ? blok.image.alt : ''"
            :zoom="true"
        />
    </a>
</template>

<style lang="scss" scoped>
    @import '~/assets/scss/image/image.scss';
</style>
